import WarningIcon from "@mui/icons-material/Warning";
import { Button, Stack } from "@mui/material";
import {
    type MRT_ColumnDef,
    type MRT_Row,
    MaterialReactTable,
    useMaterialReactTable,
} from "material-react-table";
import { useMemo, useState } from "react";

import CaseTaskDetailsDialog from "@/components/CaseTaskDetailsDialog.tsx";
import CompleteTaskForCaseButton from "@/components/CompleteTaskForCaseButton.tsx";
import ConfirmDeleteTaskModal from "@/components/ConfirmDeleteTaskModal.tsx";
import { useEmployee } from "@/contexts/EmployeeProvider.tsx";
import type { CaseTaskWithDueDate } from "@/types/caseTask.ts";
import { useNavigate } from "react-router-dom";

type Props = {
    data: CaseTaskWithDueDate[];
};

const MrtDashboardCaseTasks = ({ data }: Props) => {
    const { hasPermission } = useEmployee();
    const navigate = useNavigate();
    const [task, setTask] = useState<CaseTaskWithDueDate | null>(null);
    const [open, setOpen] = useState(false);

    const handleRowClick = (row: MRT_Row<CaseTaskWithDueDate>) => {
        setTask(row.original);
        setOpen(true);
    };

    const handleClose = () => {
        setTask(null);
        setOpen(false);
    };

    const columns = useMemo<MRT_ColumnDef<CaseTaskWithDueDate>[]>(() => {
        const columnDefs: MRT_ColumnDef<CaseTaskWithDueDate>[] = [
            {
                accessorKey: "name",
                header: "Task",
                size: 50,
            },
            {
                accessorKey: "case.id",
                header: "Case Number",
                size: 50,
                Cell: ({ renderedCellValue }) => {
                    return (
                        <Button
                            variant={"text"}
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                navigate(`/case-detail/${renderedCellValue}`);
                            }}
                        >
                            {renderedCellValue}
                        </Button>
                    );
                },
            },
            {
                accessorKey: "case.clientName",
                header: "Client Name",
                size: 50,
            },
            {
                accessorKey: "case.caseName",
                header: "Important Notes",
                size: 50,
            },
            {
                accessorKey: "metaCreatedWhen",
                header: "Date Assigned",
                Cell: ({ renderedCellValue }) => {
                    if (renderedCellValue instanceof Date) {
                        return renderedCellValue.toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                        });
                    }

                    return "";
                },
            },
            {
                accessorKey: "due",
                header: "Task Due",
                Cell: ({ row }) => {
                    // If duration is 0, show "N/A" (no due date)
                    if (row.original.duration === 0) {
                        return "N/A";
                    }

                    if (row.original.metaCreatedWhen instanceof Date) {
                        const dueDate = new Date(
                            row.original.metaCreatedWhen.getTime() +
                                row.original.duration * 24 * 60 * 60 * 1000,
                        );

                        return (
                            <Stack
                                sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    justifyContent: "flex-start",
                                    width: "100%",
                                }}
                            >
                                {dueDate.toLocaleDateString("en-US", {
                                    year: "numeric",
                                    month: "2-digit",
                                    day: "2-digit",
                                })}
                                {dueDate < new Date() && (
                                    <WarningIcon color={"error"} sx={{ ml: 2 }} />
                                )}
                            </Stack>
                        );
                    }

                    return "";
                },
            },
            {
                accessorKey: "coreRoles",
                header: "Assigned to Role",
                Cell: ({ row }) => {
                    if (row.original.coreRoles) {
                        return row.original.coreRoles.join(", ");
                    }

                    return "";
                },
            },
            {
                accessorKey: "assignedBy",
                header: "Assigned By",
            },
            {
                accessorKey: "action",
                header: "Complete Task",
                Cell: ({ row }) => {
                    if (row.original.status === "Completed") {
                        return "";
                    }

                    return (
                        <div onClick={(e) => e.stopPropagation()}>
                            <CompleteTaskForCaseButton
                                caseTaskUUID={row.original.idUUID}
                                caseStatus={"Case Details"}
                                caseUUID={row.original.case.idUUID}
                                taskName={row.original.name}
                                dueDate={row.original.dueDate}
                                description={row.original.description}
                                caseTask={row.original}
                            />
                        </div>
                    );
                },
            },
        ];

        if (hasPermission("delete:case-task")) {
            columnDefs.push({
                accessorKey: "deleteAction",
                header: "Delete Task",
                Cell: ({ row }) => {
                    return (
                        <div onClick={(e) => e.stopPropagation()}>
                            <ConfirmDeleteTaskModal caseTaskUUID={row.original.idUUID} />
                        </div>
                    );
                },
            });
        }

        return columnDefs;
    }, []);

    const table = useMaterialReactTable({
        columns,
        data: data,
        initialState: {
            showColumnFilters: false,
            showGlobalFilter: true,
            density: "compact",
        },

        enableTopToolbar: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableHiding: false,
        enableColumnFilters: false,

        manualFiltering: true,
        manualPagination: true,
        manualSorting: true,

        enablePagination: false,
        enableBottomToolbar: false,
        muiTableBodyRowProps: ({ row }: { row: MRT_Row<CaseTaskWithDueDate> }) => ({
            onClick: () => handleRowClick(row),
            sx: { cursor: "pointer" },
        }),
    });

    if (data === undefined) {
        return <div>No data</div>;
    }

    return (
        <>
            <MaterialReactTable table={table} />
            <CaseTaskDetailsDialog task={task} open={open} handleClose={handleClose} />
        </>
    );
};

export default MrtDashboardCaseTasks;
